import React, { useState } from "react";
import {
    AppBar,
    Button,
    Container,
    createStyles,
    IconButton,
    makeStyles,
    Theme,
    Toolbar,
    Typography,
} from "@material-ui/core";
import Screen1 from "./Screen1/Screen1";
import Screen2 from "./Screen2/Screen2";
import Screen3 from "./Screen3/Screen3";
import { FileObject } from "material-ui-dropzone";
import { Close } from "@material-ui/icons";
import Settings from "../Write/Settings/Settings";
import { SettingsType } from "../Write/Write";
import Cookies from "js-cookie";
import config from "../../config/apiConfig";
import { convertFromHTML } from "draft-convert";
import { convertToRaw } from "draft-js";
import { RouteComponentProps } from "react-router";
import Loader from "react-spinners/RingLoader";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            flexGrow: 1,
        },
        appBar: {
            paddingInline: theme.spacing(3),
        },
        menuButton: {
            marginRight: theme.spacing(2),
        },
        title: {
            flexGrow: 1,
        },
    })
);

const getFormData = () => {
    const formData = new FormData();
    formData.append("session-token", Cookies.get("token") ?? "");
    formData.append("token", config.token);
    return formData;
};

const Upload: React.FC<RouteComponentProps> = ({ history }) => {
    const [fileObj, setFileObj] = useState<FileObject | null>(null);
    const [html, setHtml] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [values, setValues] = useState<SettingsType>({
        cover: "",
        title: "",
        description: "",
        characters: [],
        tags: [],
        genre: "",
        audience: "",
        language: "",
        copyrights: "All Rights Reserved",
        mature: true,
        viability: false,
        protagonist: "",
        otherProtagonists: "",
        protagonistPronoun: "",
        protagonistAttr: "",
        protagonistPersonality: "",
        protagonistGoal: "",
        goalType: "",
        storyEnding: "",
        goalSuccess: "",
        protagonistScoresIntroverted: 0,
        protagonistScoresLikable: 0,
        protagonistScoresFocused: 0,
        protagonistScoresEmotional: 0,
        protagonistScoresImprovised: 0,
    });

    const classes = useStyles();
    const docRef = React.createRef<HTMLDivElement>();

    const getContainerWidth = () => {
        if (!fileObj || !!html) return "sm";
        return "md";
    };

    const handleNext = async () => {
        if (!!html) {
            setIsLoading(true);
            const raw = JSON.stringify(convertToRaw(convertFromHTML({})(html)));

            const formData = getFormData();
            const data = {
                title: values.title,
                short_description: values.description,
                language: values.language,
                genres: values.genre,
                rating: values.mature ? "mature" : "all",
                copyright: values.copyrights,
                target_audience: values.audience,
                characters: values.characters,
                tags: values.tags,
                viability: values.viability ? "private" : "public",
                protagonist_name: values.protagonist,
                other_protagonist_name: values.otherProtagonists,
                protagonist_pronoun: values.protagonistPronoun,
                protagonist_attributes: values.protagonistAttr,
                protagonist_personality: values.protagonistPersonality,
                protagonist_goal: values.protagonistGoal,
                goal_type: values.goalType,
                end_of_story: values.storyEnding,
                protagonist_success: values.goalSuccess,
                protagonist_scores: {
                    introverted: values.protagonistScoresIntroverted,
                    likable: values.protagonistScoresLikable,
                    focused: values.protagonistScoresFocused,
                    emotional: values.protagonistScoresEmotional,
                    improvised: values.protagonistScoresIntroverted,
                },
            };
            if (
                data.title === "" ||
                data.short_description === "" ||
                data.target_audience === "" ||
                data.genres === "" ||
                data.language === "" ||
                data.characters.length < 1
            ) {
                return alert("Please fill required fields!");
            }
            formData.append("payload", JSON.stringify(data));
            if (values.cover_image)
                formData.append(
                    "cover_image",
                    values.cover_image,
                    values.title.replaceAll(/\s|\n|/g, "") + ".jpg"
                );
            const requestParams = {
                method: "POST",
                body: formData,
            };
            const storyId: string = await fetch(
                config.BASE_URL + "/v1/put/writingtool/story/new",
                requestParams
            )
                .then((res) => res.json())
                .then((res) => res.data.story_id);

            const formData2 = getFormData();
            const data2 = {
                title: "The Beginning",
                chapter_number: 1,
                body: { raw, html },
            };
            formData2.append("payload", JSON.stringify(data2));
            const requestParams2 = {
                method: "POST",
                body: formData2,
            };
            const chapterId = await fetch(
                config.BASE_URL +
                    "/v1/put/writingtool/story/" +
                    storyId +
                    "/chapter/new",
                requestParams2
            )
                .then((res) => res.json())
                .then((res) => res.data.chapter_id);
            history.replace(
                `/writer/write?story_id=${storyId}&chapter_id=${chapterId}`
            );
        } else {
            setHtml(docRef.current?.innerHTML ?? "");
        }
    };

    return (
        <React.Fragment>
            <div className={classes.root}>
                <AppBar
                    className={classes.appBar}
                    color={"transparent"}
                    position="static"
                >
                    <Toolbar>
                        <IconButton
                            edge="start"
                            className={classes.menuButton}
                            color="inherit"
                            aria-label="menu"
                            onClick={() => history.goBack()}
                        >
                            <Close />
                        </IconButton>
                        <Typography variant="h6" className={classes.title}>
                            Upload your story
                        </Typography>
                        {!!fileObj && !!html && (
                            <Button
                                variant="contained"
                                style={{ marginRight: 16 }}
                                onClick={() => setHtml("")}
                            >
                                <small style={{ fontWeight: "bold" }}>
                                    Back
                                </small>
                            </Button>
                        )}
                        {!!fileObj && (
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={handleNext}
                            >
                                <small style={{ fontWeight: "bold" }}>
                                    Confirm and proceed
                                </small>
                            </Button>
                        )}
                    </Toolbar>
                </AppBar>
            </div>
            <Container maxWidth={getContainerWidth()}>
                {!fileObj && <Screen1 file={fileObj} onAdd={setFileObj} />}
                {!!fileObj && !html && (
                    <Screen2
                        file={fileObj}
                        ref={docRef}
                        replace={() => setFileObj(null)}
                    ></Screen2>
                )}
            </Container>
            {!!fileObj && !!html && (
                // <Screen3></Screen3>
                <Settings
                    values={values}
                    dismissible={false}
                    setValues={(val: SettingsType) => setValues(val)}
                    goToScrapbook={() => {}}
                />
            )}
            {isLoading && (
                <div className="backdrop">
                    <Loader color="pink" />
                </div>
            )}
        </React.Fragment>
    );
};

export default Upload;
